/**
 * @fileoverview Ввод canvas: указатель и камера для TS-runtime.
 */

import { Snd } from "@/game/audio/sound";
import {
  canLand,
  cmdGate,
  cmdLand,
  cmdTakeoff,
  cmdTaxiOut,
} from "@/game/commands/ops";
import { HOLD_SHORT, W } from "@/game/constants/world";
import type { CanvasRenderer } from "@/game/render/CanvasRenderer";
import type { Session } from "@/game/session/Session";
import type { GameHooks } from "@/game/state/types";
import { clamp, dist } from "@/game/util/math";
import type { DomShell } from "@/ui/dom/DomShell";

/**
 * Привязать pointer- и camera-контролы canvas.
 * @returns функция dispose
 */
export function bindCanvasInput(
  cv: HTMLCanvasElement,
  session: Session,
  renderer: CanvasRenderer,
  shell: DomShell,
  hooks: GameHooks,
  root: ParentNode,
): () => void {
  let lastTap = 0;

  const onDown = (ev: PointerEvent): void => {
    if (ev.button === 1 || ev.altKey || (ev.button === 0 && ev.shiftKey)) {
      ev.preventDefault();
      session.cam.dragging = true;
      session.cam.lx = ev.clientX;
      session.cam.ly = ev.clientY;
      try {
        cv.setPointerCapture(ev.pointerId);
      } catch {
        /* игнор */
      }
      return;
    }
    if (ev.button !== 0) return;
    ev.preventDefault();
    const now = performance.now();
    if (now - lastTap < 80) return;
    lastTap = now;
    Snd.init();
    Snd.resume();
    const g = session.g;
    if (!g || !g.running) return;
    const w = renderer.worldPos(ev, session.cam);
    const selected = session.selected;

    if (selected?.status === "approach") {
      for (let i = 0; i < 2; i++) {
        const r = g.runways[i]!;
        if (
          w.y > r.y - r.w / 2 - 36 &&
          w.y < r.y + r.w / 2 + 36 &&
          w.x > r.x1 - 60 &&
          w.x < r.x2 + 60
        ) {
          if (canLand(selected, r).ok) {
            cmdLand(g, hooks, selected, i);
            shell.renderCard(true);
            return;
          }
        }
      }
    }
    if (selected?.status === "awaiting_gate") {
      // Только на перроне (awaiting_gate). Во время съезда с полосы (vacating)
      // гейт — только кнопкой в карточке: иначе клик рядом с терминалом
      // сам назначал гейт и борт «сам» уезжал.
      let bestGi = -1;
      let bestD = 56;
      for (let i = 0; i < g.gates.length; i++) {
        const gate = g.gates[i]!;
        if (gate.plane && !gate.plane.dead && gate.plane !== selected) continue;
        const d = dist(w.x, w.y, gate.x, gate.y);
        if (d < bestD) {
          bestD = d;
          bestGi = i;
        }
      }
      // Если ближе другой борт — сначала выбор, не назначение гейта
      if (bestGi >= 0) {
        let nearerPlane = false;
        for (const p of g.planes) {
          if (p.dead || p === selected) continue;
          const pd = dist(w.x, w.y, p.x, p.y);
          const rad = Math.max(36, p.T.len * 0.7);
          if (pd < rad && pd < bestD) {
            nearerPlane = true;
            break;
          }
        }
        if (!nearerPlane) {
          cmdGate(g, hooks, selected, bestGi);
          shell.renderCard(true);
          return;
        }
      }
    }
    if (selected?.status === "ready") {
      for (let i = 0; i < 2; i++) {
        const hs = HOLD_SHORT[i]!;
        if (dist(w.x, w.y, hs.x, hs.y) < 54) {
          cmdTaxiOut(g, hooks, selected, i);
          shell.renderCard(true);
          return;
        }
      }
      for (let i = 0; i < 2; i++) {
        const r = g.runways[i]!;
        if (
          w.y > r.y - r.w / 2 - 30 &&
          w.y < r.y + r.w / 2 + 30 &&
          w.x > r.x1 - 40 &&
          w.x < r.x2 + 40
        ) {
          cmdTaxiOut(g, hooks, selected, i);
          shell.renderCard(true);
          return;
        }
      }
    }
    if (selected?.status === "hold_short" && selected.rwyWish != null) {
      const r = g.runways[selected.rwyWish]!;
      if (
        w.y > r.y - r.w / 2 - 36 &&
        w.y < r.y + r.w / 2 + 36 &&
        w.x > r.x1 - 40 &&
        w.x < r.x2 + 40
      ) {
        cmdTakeoff(g, hooks, selected);
        shell.renderCard(true);
        return;
      }
    }

    let best = null;
    let bd = 1e9;
    for (const p of g.planes) {
      if (p.dead) continue;
      const d = dist(w.x, w.y, p.x, p.y);
      const rad = Math.max(36, p.T.len * 0.7);
      if (d < rad && d < bd) {
        bd = d;
        best = p;
      }
    }
    if (best) {
      shell.selectPlane(best);
      return;
    }
    // В режиме назначения цели пустой клик по перрону не снимает выбор
    if (
      selected &&
      (selected.status === "awaiting_gate" ||
        selected.status === "vacating" ||
        selected.status === "approach" ||
        selected.status === "ready" ||
        selected.status === "hold_short")
    ) {
      return;
    }
    shell.selectPlane(null);
  };

  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const old = session.cam.zoom;
    session.cam.zoom = clamp(
      session.cam.zoom * (e.deltaY > 0 ? 0.9 : 1.1),
      0.7,
      1.6,
    );
    session.cam.panX *= session.cam.zoom / old;
    session.cam.panY *= session.cam.zoom / old;
  };

  const onMove = (e: PointerEvent): void => {
    if (!session.cam.dragging) return;
    session.cam.panX += e.clientX - session.cam.lx;
    session.cam.panY += e.clientY - session.cam.ly;
    session.cam.lx = e.clientX;
    session.cam.ly = e.clientY;
  };

  const onUp = (): void => {
    session.cam.dragging = false;
  };

  cv.addEventListener("pointerdown", onDown, { passive: false });
  cv.addEventListener("contextmenu", (e) => e.preventDefault());
  cv.addEventListener("wheel", onWheel, { passive: false });
  cv.addEventListener("pointermove", onMove);
  cv.addEventListener("pointerup", onUp);
  cv.addEventListener("pointercancel", onUp);

  const q = (s: string) => root.querySelector(s);
  const zi = () => {
    session.cam.zoom = clamp(session.cam.zoom * 1.15, 0.7, 1.6);
  };
  const zo = () => {
    session.cam.zoom = clamp(session.cam.zoom / 1.15, 0.7, 1.6);
  };
  const zr = () => {
    session.cam.zoom = 1;
    session.cam.panX = 0;
    session.cam.panY = 0;
  };
  q("#bZoomIn")?.addEventListener("click", zi);
  q("#bZoomOut")?.addEventListener("click", zo);
  q("#bCamReset")?.addEventListener("click", zr);

  void W;
  return () => {
    cv.removeEventListener("pointerdown", onDown);
    cv.removeEventListener("wheel", onWheel);
    cv.removeEventListener("pointermove", onMove);
    cv.removeEventListener("pointerup", onUp);
    cv.removeEventListener("pointercancel", onUp);
  };
}
